import React, { useEffect, useRef, useState } from "react";
import { ConnectComponentsProvider } from "@stripe/react-connect-js";
import {
  ConnectAccountOnboarding,
  ConnectAccountManagement,
  ConnectDocuments,
  ConnectPayoutsList,
  ConnectBalances,
  ConnectNotificationBanner,
} from "@stripe/react-connect-js";
import useStripeConnect from "../services/useStripeConnect";
import { useStateContext } from "../contexts/ContextProvider";
import { Header, LoadingSpinner, NavigateBack } from "../components";
import { apiCreateStripeAccount } from "../services/apiService";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../components/Button";

const StripeConnect = () => {
  const { stripeConnectAccount, usersProperties } = useStateContext();
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingExited, setOnboardingExited] = useState(false);
  const [connectedAccountId, setConnectedAccountId] = useState(null);
  const stripeConnectInstance = useStripeConnect(connectedAccountId);
  const hasFeteched = useRef(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const stripeComponent = searchParams.get("ui");

  const navigate = useNavigate();

  useEffect(() => {
    if (!hasFeteched.current) {
      if (stripeConnectAccount) {
        setConnectedAccountId(stripeConnectAccount.stripeId);
      } else {
        createStripeAccount();
      }
      hasFeteched.current = true;
    }
  }, []);

  const createStripeAccount = async () => {
    const response = await apiCreateStripeAccount(getStripeAddress());
    if (response.hasError) {
      console.log(response.error);
      return;
    } else {
      setConnectedAccountId(response.data.account);
    }
  };

  const getStripeAddress = () => {
    if (usersProperties[0]) {
      return {
        leaseId: null,
        street: usersProperties[0].street,
        street2: usersProperties[0].street2,
        city: usersProperties[0].city,
        state: usersProperties[0].state,
        zip: usersProperties[0].zipCode,
      };
    } else {
      return {};
    }
  };

  if (!stripeConnectInstance) {
    return <LoadingSpinner isLoading={true} />;
  }

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <section id="header" className="w-screen">
        <Header />
      </section>
      <div className="fixed top-20 left-0 right-0 z-50">
        <NavigateBack customNavigation="/account-return" />
      </div>
      <div className="flex justify-center text-2xl mt-36 capitalize">
        {stripeComponent === "new" && <span>Connect Bank Account</span>}
        {stripeComponent === "details" && <span>Banking Details</span>}
        {stripeComponent === "transfers" && <span>Recent Transfers</span>}
        {stripeComponent === "documents" && <span>Documents</span>}
        {stripeComponent === "balance" && <span>Balance</span>}
      </div>
      <div className="p-10">
        {stripeComponent === "new" || stripeComponent === "finish" ? (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <ConnectAccountOnboarding
              onLoaderStart={() => setIsLoading(false)}
              onExit={() => setOnboardingExited(true)}
            />
          </ConnectComponentsProvider>
        ) : (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            {stripeComponent === "details" && (
              <ConnectAccountManagement
                onLoaderStart={() => setIsLoading(false)}
              />
            )}
            {stripeComponent === "transfers" && (
              <ConnectPayoutsList onLoaderStart={() => setIsLoading(false)} />
            )}
            {stripeComponent === "documents" && (
              <ConnectDocuments onLoaderStart={() => setIsLoading(false)} />
            )}
            {stripeComponent === "balance" && (
              <ConnectBalances onLoaderStart={() => setIsLoading(false)} />
            )}
          </ConnectComponentsProvider>
        )}
        {onboardingExited && (
          <div className="flex justify-center">
            <Button
              background="bg-snappily-orange"
              text="text-white"
              opacity="bg-opacity-100"
              title="Finished"
              height="h-10"
              width="w-24"
              borderColor="border-none"
              borderHover="hover:border-snappily-orange"
              customFunc={() => navigate("/account-return")}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default StripeConnect;
